import React, { useMemo } from "react";
import StatusFormatter from "../utils/StatusFormatter";
import { parseCVSSVector } from "../../forms/inputs/SeverityScoreSelector";
import RichTextRenderer from "../../common/RichTextRenderer";

interface RetestHeaderProps {
  reportName?: string;
  programName?: string;
  severityScore?: string;
  severityCategory?: string;
  retestStatus?: string | null;
  retestDate?: string | null;
  // onClick?: () => void;
}

export const RetestHeader: React.FC<RetestHeaderProps> = ({
  reportName,
  programName,
  severityScore,
  severityCategory,
  retestStatus,
  retestDate
  // onClick = () => {}
}) => {
  const { score, category: derivedCategory } = useMemo(() => {
    if (severityScore && severityCategory && !severityScore.includes("CVSS")) {
      return { score: severityScore, category: severityCategory };
    }
    // Handle legacy or CVSS 4.0 case where score/category is embedded in a vector string
    if (severityScore && severityScore.includes("CVSS")) {
      try {
        const parsed = parseCVSSVector(severityScore);
        if (parsed && parsed.score && parsed.score !== "-.-") {
          return {
            score: parsed.score,
            category: parsed.category || severityCategory || "Info"
          };
        }
      } catch (e) {
        console.warn("Failed to parse severity vector:", e);
      }
    }
    return { score: severityScore || "0.0", category: severityCategory || "Info" };
  }, [severityScore, severityCategory]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "QA Review In Process":
      case "Retest In Process":
      case "Fix in Progress":
      case "Reopen Retest":
      case "Request Further Action":
      case "Need Information":
        return "bg-[#0132f5b0] border-[3px] border-blue-600 text-white";
      case "Fix Approved":
      case "Fix Verified":
      case "Risk Accepted and Closed":
        return "bg-[#23b92f] border-[3px] border-green-600 text-white";
      case "Fix Rejected":
      case "Fix Failed":
      case "Findings Rejected":
      case "Close Retest":
        return "bg-[#F50101B2] border-[3px] border-red-600 text-white";
      default:
        return "bg-blue-600 text-white";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "QA Review In Process":
        return "QA is reviewing the retest";
      case "Retest In Process":
        return "Retesting is currently in progress";
      case "Fix in Progress":
        return "The fix is being implemented";
      case "Reopen Retest":
        return "Retest has been reopened for review";
      case "Request Further Action":
        return "Further actions are requested";
      case "Need Information":
        return "Additional information is required";
      case "Fix Approved":
        return "The fix has been approved";
      case "Fix Verified":
        return "The fix has been verified";
      case "Risk Accepted and Closed":
        return "Risk accepted, retest closed";
      case "Fix Rejected":
        return "The fix has been rejected";
      case "Fix Failed":
        return "The fix attempt has failed";
      case "Findings Rejected":
        return "Report findings were rejected";
      case "Close Retest":
        return "Retest process has been closed";
      case "Retest Requested":
        return "Retest process has been requested";
      default:
        return "Status is currently unknown";
    }
  };


  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  const getDaysAgo = (dateString: string) => {
    const today = new Date();
    const retestDate = new Date(dateString);
    const diffTime = Math.abs(today.getTime() - retestDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} days ago`;
  };

  const truncateString = (str: string, maxLength: number) => {
    if (str.length > maxLength) {
      return str.substring(0, maxLength) + "...";
    }
    return str;
  };

  return (
    <div>
      <div
        className="cursor-pointer overflow-hidden rounded-lg"
        style={{ backgroundColor: "#010D2C" }}
      // onClick={onClick}
      >
        <div className="px-6 py-4">
          <div className="-mx-3 flex flex-wrap">
            <div className="mb-4 w-full px-3 md:mb-0 md:w-3/12" style={{ borderRight: '2px solid #D2D1D1' }}>
              <p className="mb-1 text-sm" style={{ color: '#58CC02' }}>Report Title</p>
              <div
                className="text-xl font-normal capitalize text-white"
                style={{ fontWeight: "400" }}
              >
                {reportName ? <RichTextRenderer content={reportName} className="line-clamp-2" /> : "N/A"}
              </div>
            </div>
            <div className="mb-4 flex w-full flex-col gap-2 px-6 md:mb-0 md:w-3/12" style={{ borderRight: '2px solid #D2D1D1' }}>
              <p
                className="mb-1 text-sm"
                style={{ color: '#58CC02', fontWeight: "500" }}
              >
                Retest request date
              </p>
              <p
                className="text-xl font-normal capitalize text-white"
                style={{ fontWeight: "400" }}
              >
                {retestDate ? formatDate(retestDate) : "N/A"}
              </p>
              <div className="text-sm text-white" style={{ fontWeight: "500" }}>
                {retestDate ? getDaysAgo(retestDate) : "N/A"}
              </div>
            </div>
            <div className="mb-4 w-full px-6 md:mb-0 md:w-3/12" style={{ borderRight: '2px solid #D2D1D1' }}>
              <p
                className="mb-1 text-sm"
                style={{ color: '#58CC02', fontWeight: "500" }}
              >
                Severity
              </p>
              {(() => {
                const sevRaw = (derivedCategory || '').toUpperCase().trim();
                let color = '#8592a4ff', label = 'Info'; // Default to Info (Gray)

                if (sevRaw === 'CRITICAL') { color = '#F0142F'; label = 'Critical'; }
                else if (sevRaw === 'HIGH') { color = '#FF6B00'; label = 'High'; }
                else if (sevRaw === 'MEDIUM') { color = '#F2B200'; label = 'Medium'; }
                else if (sevRaw === 'LOW') { color = '#2BBF6D'; label = 'Low'; }
                else if (sevRaw === 'INFO' || sevRaw === 'NONE' || sevRaw === '') { color = '#8592a4ff'; label = 'Info'; }

                const bgColor = color + '4D'; // ~30% opacity
                return (
                  <>
                    <div className="mt-2 inline-flex items-center rounded-md border px-4 py-1.5" style={{ borderColor: color, background: bgColor }}>
                      <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: color }}></span>
                      <span className="text-sm font-normal" style={{ color: color }}>{derivedCategory} ({score})</span>
                    </div>
                    <div className="py-3 text-sm text-white" style={{ fontWeight: 500 }}>
                      CTB&apos;s vulnerability score
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="w-full px-6 md:w-3/12">
              <p
                className="mb-1 text-sm"
                style={{ color: '#58CC02', fontWeight: "500" }}
              >
                Status
              </p>
              {(() => {
                const status = retestStatus || "";
                let color = '#1662F6'; // default blue for in-process
                switch (status) {
                  case 'Fix Approved':
                  case 'Fix Verified':
                  case 'Risk Accepted and Closed':
                    color = '#2BBF6D'; // green success
                    break;
                  case 'Fix Rejected':
                  case 'Fix Failed':
                  case 'Findings Rejected':
                  case 'Close Retest':
                    color = '#F0142F'; // red error
                    break;
                  case 'QA Review In Process':
                  case 'Retest In Process':
                  case 'Fix in Progress':
                  case 'Reopen Retest':
                  case 'Request Further Action':
                  case 'Need Information':
                  default:
                    color = '#1662F6'; // blue in-progress
                    break;
                }
                const bgColor = color + '4D';
                return (
                  <div className="mt-2 inline-flex items-center rounded-md border px-4 py-1.5"
                    style={{ borderColor: color, background: bgColor }}>
                    <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: color }}></span>
                    <span className="text-sm font-normal" style={{ color: color }}>
                      <StatusFormatter status={status} />
                    </span>
                  </div>
                );
              })()}
              <div className="py-3 text-sm text-white" style={{ fontWeight: "500" }}>
                {getStatusText(retestStatus || "")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
